import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router';
import { Input } from '@elements/Input/Input';
import { Select } from '@elements/Dropdown/Select';
import StateSelect from '@elements/Dropdown/StateSelect';
import type { DropdownOption } from '@elements/Dropdown';
import StartOver from '@/assets/icons/StartOver.svg';
import Image from 'next/image';
import { Elements } from '@elements/index.ts';
import { PlansSection, type PlanWithAgePremiums } from '../PlansSection';
import featuresBackground from '@images/hero-bg.webp';
import { SkeletonPlansSection } from '../PlansSection/SkeletonPlansSection';
import {
    getCountiesByState,
    getCountyCodeBySlug,
    getCountyNameByCode,
    getCountySlugByCode,
} from '@/utils/counties';
import type { FullPlan } from '@/types/FullPlan';
import LogoRightSide from '@/assets/icons/LogoRightSide.svg';
import FilterIcon from '@/assets/icons/Filter.svg';
import {
    buildShopPlansCountyPath,
    buildShopPlansStatePath,
    buildShopPlansStateRootPath,
    normalizeCountySlug,
    parseShopPlansPath,
    SHOP_PLANS_BASE_PATH,
} from '@/lib/shopPlansRouting';
import { enrichPlansWithVisitBenefits } from '@/utils/enrichPlanVisitBenefits';

type ShopPlansData = {
    zipCode?: string;
    age?: string;
    ages?: string[];
    familyMembersCount?: number;
    state?: string;
    county?: string;
};

export interface PlansTabsWidgetProps {
    onTabChange?: (activeTab: string) => void;
    defaultTab?: string;
    loading?: boolean;
    error?: string | null;
    showPlans?: boolean;
    plans?: PlanWithAgePremiums[];
    isStateCountrySearch?: boolean;
    familyAges?: string[];
    showLoadingAnimation?: boolean;
    onShopPlans?: (data: {
        zipCode?: string;
        age?: string;
        ages?: string[];
        familyMembersCount?: number;
        state?: string;
        county?: string;
    }) => void;
}

export const PlansTabsWidget = ({
    onTabChange,
    defaultTab = 'age-zip',
    loading: externalLoading,
    error: externalError,
    showPlans: externalShowPlans,
    plans: externalPlans,
    isStateCountrySearch: externalIsStateCountrySearch,
    familyAges: externalFamilyAges,
    showLoadingAnimation: externalShowLoadingAnimation,
    onShopPlans,
}: PlansTabsWidgetProps) => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

    // `compare` is used by PlansSection for the compare modal and should not trigger
    // URL-driven hydration / auto-loading logic in this widget (it can feel like a "page reload").
    const searchParamsKeyWithoutCompare = useMemo(() => {
        const sp = new URLSearchParams(searchParams);
        sp.delete('compare');
        sp.delete('currentPlan');
        sp.delete('employerContribution');
        return sp.toString();
    }, [searchParams]);

    // Detect desktop to avoid mounting heavy map on mobile
    const [isDesktop, setIsDesktop] = useState<boolean>(() => {
        if (typeof window === 'undefined') return true;
        return window.innerWidth >= 1024;
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handleResize = () => {
            setIsDesktop(window.innerWidth >= 1024);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const parsePathParams = () => {
        const parsedPath = parseShopPlansPath(location.pathname);

        if (parsedPath.kind === 'zip') {
            return {
                zipCode: parsedPath.zipCode || '',
                age: '',
                state: '',
                county: '',
                hasStatePath: false,
                isStateRootPath: false,
            };
        }

        if (parsedPath.kind === 'state-root') {
            return {
                zipCode: '',
                age: '',
                state: '',
                county: '',
                hasStatePath: true,
                isStateRootPath: true,
            };
        }

        if (parsedPath.kind === 'state' || parsedPath.kind === 'state-county') {
            return {
                zipCode: '',
                age: '',
                state: parsedPath.stateCode || '',
                county: parsedPath.countySlug || '',
                hasStatePath: true,
                isStateRootPath: false,
            };
        }

        return {
            zipCode: searchParams.get('zipCode') || '',
            age: '',
            state: searchParams.get('state') || '',
            county: searchParams.get('county') || '',
            hasStatePath: false,
            isStateRootPath: false,
        };
    };

    const getInitialState = () => {
        const pathParams = parsePathParams();
        const urlZipCode = pathParams.zipCode || searchParams.get('zipCode');
        const urlAges = searchParams.getAll('age');
        const urlState = pathParams.state || searchParams.get('state');
        const urlCounty = pathParams.county || searchParams.get('county');
        const urlTab = searchParams.get('tab');
        const hasStatePath = pathParams.hasStatePath;
        const isStateRootPath = pathParams.isStateRootPath;

        // If tab is explicitly set in URL, use it.
        if (urlTab === 'age-zip' || urlTab === 'state-county') {
            const baseState = {
                activeTab: urlTab as string,
                zipCode: urlZipCode || '',
                ages: urlAges.length > 0 ? urlAges : [''],
                selectedState: urlState || '',
                selectedCounty: urlCounty || '',
            };

            // If switching to age-zip but have state/county params, clear them
            if (urlTab === 'age-zip') {
                baseState.selectedState = '';
                baseState.selectedCounty = '';
            }
            // If switching to state-county but have zip/age params, clear them
            if (urlTab === 'state-county') {
                baseState.zipCode = '';
                baseState.ages = [''];
            }

            return baseState;
        }

        if (urlZipCode || urlAges.length > 0) {
            return {
                activeTab: 'age-zip' as string,
                zipCode: urlZipCode || '',
                ages: urlAges.length > 0 ? urlAges : [''],
                selectedState: '',
                selectedCounty: '',
            };
        } else if (isStateRootPath || hasStatePath || urlState || urlCounty) {
            return {
                activeTab: 'state-county' as string,
                zipCode: '',
                ages: [''],
                selectedState: urlState || '',
                selectedCounty: urlCounty || '',
            };
        }

        return {
            activeTab: defaultTab,
            zipCode: '',
            ages: [''],
            selectedState: '',
            selectedCounty: '',
        };
    };

    const initialState = getInitialState();
    const [activeTab, setActiveTab] = useState<string>(initialState.activeTab);
    const [zipCode, setZipCode] = useState<string>(initialState.zipCode);
    const [ages, setAges] = useState<string[]>(initialState.ages);
    const [selectedState, setSelectedState] = useState<string>(initialState.selectedState);
    const [selectedCounty, setSelectedCounty] = useState<string>(initialState.selectedCounty);
    const [countyOptions, setCountyOptions] = useState<DropdownOption[]>([]);
    const [isLoadingCounties, setIsLoadingCounties] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<{
        zipCode?: boolean;
        age?: boolean;
        state?: boolean;
    }>({});

    const [internalPlans, setInternalPlans] = useState<PlanWithAgePremiums[]>([]);
    const [internalLoading, setInternalLoading] = useState(false);
    const [internalError, setInternalError] = useState<string | null>(null);
    const [internalShowPlans, setInternalShowPlans] = useState(false);
    const [resultsTab, setResultsTab] = useState<'age-zip' | 'state-county' | null>(null);
    const [internalIsStateCountrySearch, setInternalIsStateCountrySearch] = useState(false);
    const [internalShowLoadingAnimation, setInternalShowLoadingAnimation] = useState(false);
    const [internalFamilyAges, setInternalFamilyAges] = useState<string[]>([]);
    const [isContentVisible, setIsContentVisible] = useState(false);
    const [hasStickyBarTakenOver, setHasStickyBarTakenOver] = useState(false);
    const isUpdatingUrlRef = useRef(false);
    const lastSearchKeyRef = useRef<string>('');
    const contentTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const fetchingAgePremiumsRef = useRef<string>('');
    const activeFetchRef = useRef<string>('');
    const isHydratingFromUrlRef = useRef(true);
    const stickyBarBoundaryRef = useRef<HTMLElement>(null);
    const stickyBarTakenOverRef = useRef(false);

    const loading = externalLoading !== undefined ? externalLoading : internalLoading;
    const error = externalError !== undefined ? externalError : internalError;
    const showPlans = externalShowPlans !== undefined ? externalShowPlans : internalShowPlans;
    const plans = externalPlans !== undefined && externalPlans.length > 0 ? externalPlans : internalPlans;
    const isStateCountrySearch = externalIsStateCountrySearch !== undefined ? externalIsStateCountrySearch : internalIsStateCountrySearch;
    const showLoadingAnimation = externalShowLoadingAnimation !== undefined ? externalShowLoadingAnimation : internalShowLoadingAnimation;
    const familyAges = externalFamilyAges !== undefined && externalFamilyAges.length > 0 ? externalFamilyAges : internalFamilyAges;

    const isExternallyControlled = useMemo(() => {
        return (
            externalLoading !== undefined ||
            externalError !== undefined ||
            externalShowPlans !== undefined ||
            externalPlans !== undefined
        );
    }, [externalError, externalLoading, externalPlans, externalShowPlans]);

    // Ensure results never render under the wrong tab, even if a stale async path tries to set state.
    const effectiveShowPlans = useMemo(() => {
        if (isExternallyControlled) return showPlans;
        return Boolean(showPlans && resultsTab === activeTab);
    }, [activeTab, isExternallyControlled, resultsTab, showPlans]);

    // Prevent skeleton/loading UI from showing when switching tabs (old-tab in-flight requests).
    const effectiveLoading = useMemo(() => {
        if (isExternallyControlled) return loading;
        return Boolean(loading && resultsTab === activeTab);
    }, [activeTab, isExternallyControlled, loading, resultsTab]);

    const effectiveShowLoadingAnimation = useMemo(() => {
        if (isExternallyControlled) return showLoadingAnimation;
        return Boolean(showLoadingAnimation && resultsTab === activeTab);
    }, [activeTab, isExternallyControlled, resultsTab, showLoadingAnimation]);

    useEffect(() => {
        if (contentTimeoutRef.current) {
            clearTimeout(contentTimeoutRef.current);
        }

        if (effectiveShowPlans && !effectiveLoading && plans.length > 0) {
            contentTimeoutRef.current = setTimeout(() => {
                setIsContentVisible(true);
            }, 50);
        } else {
            setIsContentVisible(false);
        }

        return () => {
            if (contentTimeoutRef.current) {
                clearTimeout(contentTimeoutRef.current);
            }
        };
    }, [effectiveShowPlans, effectiveLoading, plans.length]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const syncStickyBarState = (nextActive: boolean) => {
            if (stickyBarTakenOverRef.current === nextActive) {
                return;
            }

            stickyBarTakenOverRef.current = nextActive;
            setHasStickyBarTakenOver(nextActive);
            window.dispatchEvent(new CustomEvent('shop-plans-sticky-change', {
                detail: { active: nextActive },
            }));
        };

        const getHeaderHeight = () => {
            const cssValue = window.getComputedStyle(document.documentElement).getPropertyValue('--site-header-height');
            const parsedHeight = Number.parseFloat(cssValue);

            if (Number.isFinite(parsedHeight)) {
                return parsedHeight;
            }

            const headerEl = document.querySelector<HTMLElement>('[data-site-header]');
            return headerEl?.offsetHeight ?? 0;
        };

        let frameId: number | null = null;

        const updateStickyState = () => {
            frameId = null;

            const boundaryEl = stickyBarBoundaryRef.current;
            if (!boundaryEl) {
                syncStickyBarState(false);
                return;
            }

            const nextActive = boundaryEl.getBoundingClientRect().top <= getHeaderHeight();
            syncStickyBarState(nextActive);
        };

        const requestStickyStateUpdate = () => {
            if (frameId !== null) {
                return;
            }

            frameId = window.requestAnimationFrame(updateStickyState);
        };

        requestStickyStateUpdate();
        window.addEventListener('scroll', requestStickyStateUpdate, { passive: true });
        window.addEventListener('resize', requestStickyStateUpdate);

        return () => {
            if (frameId !== null) {
                window.cancelAnimationFrame(frameId);
            }

            window.removeEventListener('scroll', requestStickyStateUpdate);
            window.removeEventListener('resize', requestStickyStateUpdate);
            syncStickyBarState(false);
        };
    }, []);

    const isFirstRender = useRef(true);

    useEffect(() => {
        const parsedPath = parseShopPlansPath(location.pathname);
        const hasSearchContext =
            Boolean(searchParams.get('tab')) ||
            Boolean(searchParams.get('zipCode')) ||
            searchParams.getAll('age').length > 0 ||
            Boolean(searchParams.get('state')) ||
            Boolean(searchParams.get('county'));

        if (parsedPath.kind !== 'base' || hasSearchContext) {
            return;
        }

        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('tab', 'age-zip');
        const qs = nextSearchParams.toString();
        const nextHref = qs ? `${SHOP_PLANS_BASE_PATH}?${qs}` : SHOP_PLANS_BASE_PATH;

        isUpdatingUrlRef.current = true;
        navigate(nextHref, { replace: true, preventScrollReset: true });
    }, [location.pathname, navigate, searchParams]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (isUpdatingUrlRef.current) {
            return;
        }

        const pathParams = parsePathParams();

        const urlZipCode = pathParams.zipCode || searchParams.get('zipCode');
        const urlAges = searchParams.getAll('age');
        let urlState = pathParams.state || searchParams.get('state');
        if (urlState && urlState.includes('-')) {
            urlState = urlState.split('-')[0].toUpperCase();
        }
        const urlCounty = pathParams.county || searchParams.get('county');
        const urlTab = searchParams.get('tab');
        const hasStatePath = pathParams.hasStatePath;
        const isStateRootPath = pathParams.isStateRootPath;

        // If tab is explicitly set in URL, use it
        if (urlTab === 'age-zip' || urlTab === 'state-county') {
            setActiveTab(urlTab);
            if (urlTab === 'age-zip') {
                if (urlZipCode) setZipCode(urlZipCode);
                if (urlAges.length > 0) {
                    setAges(urlAges);
                } else {
                    setAges(['']);
                }
                setSelectedState('');
                setSelectedCounty('');
            } else if (urlTab === 'state-county') {
                if (urlState) setSelectedState(urlState);
                if (urlCounty) setSelectedCounty(urlCounty);
                setZipCode('');
                setAges(['']);
            }
            return;
        }

        if (!urlZipCode && urlAges.length === 0 && !urlState && !urlCounty && !hasStatePath) {

            if (!isUpdatingUrlRef.current) {

                if (activeTab === 'state-county' && selectedState) {
                    setZipCode('');
                    setAges(['']);
                } else {
                    setActiveTab(defaultTab);
                    setZipCode('');
                    setAges(['']);
                    setSelectedState('');
                    setSelectedCounty('');
                }
            } else {
                setZipCode('');
                setAges(['']);
                setSelectedState('');
                setSelectedCounty('');
            }
            setInternalPlans([]);
            setInternalShowPlans(false);
            setInternalError(null);
            return;
        }

        const isUserOnStateCountyTab = activeTab === 'state-county';

        if (urlZipCode || urlAges.length > 0) {

            if (!isUserOnStateCountyTab || !selectedState) {
                setActiveTab('age-zip');
            }
            if (urlZipCode) setZipCode(urlZipCode);
            if (urlAges.length > 0) {
                setAges(urlAges);
            }

            if (!urlState && !urlCounty && !isUserOnStateCountyTab) {
                setSelectedState('');
                setSelectedCounty('');
            }
        } else if (isStateRootPath || hasStatePath || urlState || urlCounty) {

            if (urlTab !== 'age-zip' && activeTab !== 'age-zip') {
                setActiveTab('state-county');
            }
            if (urlState) setSelectedState(urlState);
            if (urlCounty) setSelectedCounty(urlCounty);

            if (!urlZipCode && urlAges.length === 0) {
                setZipCode('');
                setAges(['']);
            }
        }
    }, [searchParamsKeyWithoutCompare, location.pathname]);

    useEffect(() => {
        const handleSwitchToStateCountyTab = () => {
            setActiveTab('state-county');
            setSelectedState('');
            setSelectedCounty('');
        };

        window.addEventListener('switchToStateCountyTab', handleSwitchToStateCountyTab as EventListener);

        return () => {
            window.removeEventListener('switchToStateCountyTab', handleSwitchToStateCountyTab as EventListener);
        };
    }, []);

    const handleStartOver = () => {
        // Clear fields based on current active tab
        if (activeTab === 'age-zip') {
            // Clear zip and age tab fields
            setZipCode('');
            setAges(['']);
            setValidationError(null);
            setFieldErrors({});
            setInternalShowPlans(false);
            setInternalPlans([]);
        } else if (activeTab === 'state-county') {
            // Clear state and county tab fields
            setSelectedState('');
            setSelectedCounty('');
            setCountyOptions([]);
            setValidationError(null);
            setFieldErrors({});
            setInternalShowPlans(false);
            setInternalPlans([]);
        }
    };

    useEffect(() => {
        if (!selectedState) {
            setCountyOptions([]);
            return;
        }

        setIsLoadingCounties(true);
        getCountiesByState(selectedState)
            .then((options) => {
                setCountyOptions(options);
                setSelectedCounty((prev) => {
                    if (!prev) return '';


                    const foundByValue = options.find((opt) => opt.value === prev);
                    if (foundByValue) return prev;



                    const prevCountySlug = normalizeCountySlug(prev);
                    const foundBySlug = options.find((opt) => {
                        if (typeof opt.label !== 'string') return false;
                        const optionSlug = normalizeCountySlug(opt.label);
                        if (optionSlug === prevCountySlug) return true;

                        const optionSlugNoSuffix = optionSlug.replace(
                            /-(county|parish|borough|census-area|city-and-borough|municipality|city)$/i,
                            ''
                        );

                        return optionSlugNoSuffix === prevCountySlug;
                    });
                    if (foundBySlug) return foundBySlug.value;

                    return '';
                });
            })
            .catch((error) => {
                console.error('Failed to load counties:', error);
                setCountyOptions([]);
            })
            .finally(() => {
                setIsLoadingCounties(false);
            });
    }, [selectedState]);

    const handleTabChange = (tabValue: string) => {
        // Always clear previous results on tab switch so each tab has isolated results.
        // Also invalidates in-flight requests (see stale guard in handleShopPlansInternal).
        setInternalPlans([]);
        setInternalShowPlans(false);
        setResultsTab(null);
        setInternalLoading(false);
        setInternalShowLoadingAnimation(false);
        setInternalError(null);
        setValidationError(null);
        setInternalFamilyAges([]);
        setInternalIsStateCountrySearch(false);
        activeFetchRef.current = '';
        fetchingAgePremiumsRef.current = '';
        lastSearchKeyRef.current = '';

        // Navigate to the base route and remove tab-specific inputs so the destination tab is "blank".
        isUpdatingUrlRef.current = true;
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('tab');
        newSearchParams.delete('zipCode');
        newSearchParams.delete('age'); // removes all occurrences
        newSearchParams.delete('state');
        newSearchParams.delete('county');

        setZipCode('');
        setAges(['']);
        setSelectedState('');
        setSelectedCounty('');
        setCountyOptions([]);

        let basePath = SHOP_PLANS_BASE_PATH;
        if (tabValue === 'age-zip') {
            newSearchParams.set('tab', 'age-zip');
        } else if (tabValue === 'state-county') {
            basePath = buildShopPlansStateRootPath();
        }
        const qs = newSearchParams.toString();
        navigate(qs ? `${basePath}?${qs}` : basePath, { replace: true });

        setActiveTab(tabValue);
        onTabChange?.(tabValue);
    };

    const normalizeAge = (age: string | number): number => {
        const ageNum = typeof age === 'string' ? Number(age) : age;
        return ageNum >= 66 ? 65 : ageNum;
    };

    const fetchPlans = async (data: ShopPlansData) => {
        const body: Record<string, string | number | number[]> = {};

        if (data.zipCode) body.zip_code = data.zipCode;
        if (data.state) body.state = data.state;
        if (data.county) body.county = data.county;
        if (data.age) body.age = normalizeAge(data.age);

        if (data.ages && data.ages.length > 1) {
            const validAges = data.ages.filter((age) => age.trim() !== '');
            if (validAges.length > 1) {
                const dependantAges = validAges.slice(1).map((age) => normalizeAge(age));
                body.dependant_ages = dependantAges;
            }

        }

        const response = await fetch('https://rates-public.predictablebenefits.com/public/plans/medical/search', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            throw new Error('Failed to fetch plans');
        }

        const result = await response.json();
        const plansFromApi: FullPlan[] = Array.isArray(result)
            ? result
            : Array.isArray(result?.plans)
                ? result.plans
                : [];

        if (plansFromApi.length === 0) {
            return result;
        }

        const enrichedPlans = await enrichPlansWithVisitBenefits(plansFromApi);
        if (Array.isArray(result)) {
            return enrichedPlans;
        }

        return {
            ...result,
            plans: enrichedPlans,
        };
    };

    const buildNextSearchParamsForSearch = useCallback((data: ShopPlansData) => {
        // Preserve existing query params (including filters) but IGNORE `compare`.
        // `compare` is owned by PlansSection and should not influence search loading logic.
        const params = new URLSearchParams(searchParamsKeyWithoutCompare);
        params.delete('age');

        const validAges = data.ages && data.ages.length > 0
            ? data.ages.filter((age) => age.trim() !== '')
            : (data.age ? [data.age] : []);

        validAges.forEach((age) => params.append('age', age));
        return params;
    }, [searchParamsKeyWithoutCompare]);

    const handleShopPlansInternal = useCallback(
        async (data: ShopPlansData, options?: { updateUrl?: boolean }) => {
            const updateUrl = options?.updateUrl ?? true;
            const searchKey = `${data.zipCode || ''}-${data.state || ''}-${data.county || ''}-${data.age || ''}-${data.ages?.join(',') || ''}`;
            const isStale = () => activeFetchRef.current !== searchKey;

            if (activeFetchRef.current === searchKey) {
                return;
            }

            activeFetchRef.current = searchKey;
            const isAgeZipTab = Boolean(data.zipCode || data.age || (data.ages && data.ages.length > 0));
            const isStateCountyTab = Boolean(data.state || data.county);
            const ownerTab: 'age-zip' | 'state-county' = isStateCountyTab ? 'state-county' : 'age-zip';
            setResultsTab(ownerTab);

            if (isAgeZipTab && data.zipCode) {
                if (updateUrl && !isHydratingFromUrlRef.current) {
                    const params = buildNextSearchParamsForSearch(data);
                    // Age/ZIP tab: keep age in query, move ZIP into the path segment.
                    params.set('tab', 'age-zip');
                    params.delete('zipCode');
                    params.delete('state');
                    params.delete('county');
                    const qs = params.toString();
                    const basePath = `${SHOP_PLANS_BASE_PATH}/zip/${encodeURIComponent(data.zipCode)}`;
                    const newPath = qs ? `${basePath}?${qs}` : basePath;
                    isUpdatingUrlRef.current = true;
                    navigate(newPath, { replace: true });
                    setTimeout(() => {
                        isUpdatingUrlRef.current = false;
                    }, 50);
                }
            } else if (isStateCountyTab && data.state) {
                const stateCode = data.state.toUpperCase();
                let countySlug = '';
                if (data.county) {
                    countySlug = (await getCountySlugByCode(stateCode, data.county)) ?? '';

                    if (!countySlug) {
                        const countyOption = countyOptions.find((opt) => opt.value === data.county);
                        if (countyOption && typeof countyOption.label === 'string') {
                            countySlug = normalizeCountySlug(countyOption.label);
                        }
                    }

                    if (!countySlug) {
                        try {
                            const countyName = await getCountyNameByCode(stateCode, data.county);
                            countySlug = countyName ? normalizeCountySlug(countyName) : normalizeCountySlug(data.county);
                        } catch {
                            countySlug = normalizeCountySlug(data.county);
                        }
                    }
                }

                if (updateUrl && !isHydratingFromUrlRef.current) {
                    const params = buildNextSearchParamsForSearch({ ...data, ages: [] });
                    params.delete('tab');
                    params.delete('state');
                    params.delete('county');
                    params.delete('zipCode');
                    params.delete('age');
                    const basePath = countySlug
                        ? buildShopPlansCountyPath(stateCode, countySlug)
                        : buildShopPlansStatePath(stateCode);
                    const qs = params.toString();
                    const newPath = qs ? `${basePath}?${qs}` : basePath;
                    isUpdatingUrlRef.current = true;
                    navigate(newPath, { replace: true });
                    setTimeout(() => {
                        isUpdatingUrlRef.current = false;
                    }, 50);
                }
            }

            setInternalLoading(true);
            setInternalError(null);
            setInternalShowPlans(false);
            setInternalPlans([]);
            setInternalShowLoadingAnimation(false);

            const hasNoAge = !data.age && (!data.ages || data.ages.length === 0 || data.ages.every(age => age.trim() === ''));
            const isStateCountrySearch = isStateCountyTab || (isAgeZipTab && hasNoAge);
            setInternalIsStateCountrySearch(isStateCountrySearch);

            requestAnimationFrame(() => {
                setTimeout(() => {
                    const plansSection = document.getElementById('plans-section');
                    if (plansSection) {
                        const elementTop = plansSection.getBoundingClientRect().top + window.pageYOffset;
                        const headerOffset = 300;
                        window.scrollTo({
                            top: Math.max(0, elementTop - headerOffset),
                            behavior: 'smooth',
                        });
                    }
                }, 100);
            });

            if (data.ages && data.ages.length > 0) {
                setInternalFamilyAges(data.ages.filter((age) => age.trim() !== ''));
            } else if (data.age) {
                setInternalFamilyAges([data.age]);
            } else {
                setInternalFamilyAges([]);
            }

            const animationTimeout = setTimeout(() => {
                setInternalShowLoadingAnimation(true);
            }, 2000);

            try {
                const hasNoAge = !data.age && (!data.ages || data.ages.length === 0 || data.ages.every(age => age.trim() === ''));
                const isStateCountry = Boolean((!data.age && !data.zipCode && (data.state || data.county)) || (data.zipCode && hasNoAge));
                setInternalIsStateCountrySearch(isStateCountry);

                const result = await fetchPlans(data);
                if (isStale()) return;
                const plansFromApi: FullPlan[] = result.plans ?? result;

                if (isStateCountry) {
                    const fetchKey = `${data.zipCode || ''}-${data.state || ''}-${data.county || ''}`;

                    if (fetchingAgePremiumsRef.current === fetchKey) {
                        if (isStale()) return;
                        setInternalPlans(plansFromApi);
                        setInternalShowPlans(true);
                        setInternalLoading(false);
                        setInternalShowLoadingAnimation(false);
                        if (animationTimeout) clearTimeout(animationTimeout);
                        return;
                    }

                    fetchingAgePremiumsRef.current = fetchKey;


                    const fetchParamsNoAge = data.zipCode
                        ? { zipCode: data.zipCode }
                        : { state: data.state, county: data.county };

                    fetchPlans(fetchParamsNoAge)
                        .then((result) => {
                            if (isStale()) return;



                            let plans18: FullPlan[] = [];
                            let plans65: FullPlan[] = [];


                            if (result['18'] && result['65']) {
                                plans18 = Array.isArray(result['18']) ? result['18'] : (result['18'].plans ?? []);
                                plans65 = Array.isArray(result['65']) ? result['65'] : (result['65'].plans ?? []);
                            }

                            else if (result.age_18 && result.age_65) {
                                plans18 = Array.isArray(result.age_18) ? result.age_18 : (result.age_18.plans ?? []);
                                plans65 = Array.isArray(result.age_65) ? result.age_65 : (result.age_65.plans ?? []);
                            }

                            else if (result.plans && Array.isArray(result.plans) && result.plans.length > 0) {
                                const allPlans: any[] = result.plans;

                                if (allPlans[0] && ('premium_18' in allPlans[0] || 'premiumAge18' in allPlans[0])) {

                                    plans18 = allPlans.map((p: any) => ({ ...p, premium: p.premium_18 || p.premiumAge18 || p.premium }));
                                    plans65 = allPlans.map((p: any) => ({ ...p, premium: p.premium_65 || p.premiumAge65 || p.premium }));
                                } else {

                                    const fetchParams18 = data.zipCode
                                        ? { zipCode: data.zipCode, age: '18' }
                                        : { state: data.state, county: data.county, age: '18' };

                                    const fetchParams65 = data.zipCode
                                        ? { zipCode: data.zipCode, age: '65' }
                                        : { state: data.state, county: data.county, age: '65' };

                                    Promise.all([
                                        fetchPlans(fetchParams18).catch(() => ({ plans: [] })),
                                        fetchPlans(fetchParams65).catch(() => ({ plans: [] })),
                                    ])
                                        .then(([result18, result65]) => {
                                            const plans18Data: FullPlan[] = result18.plans ?? result18;
                                            const plans65Data: FullPlan[] = result65.plans ?? result65;
                                            processAgePremiums(plans18Data, plans65Data);
                                        })
                                        .catch((err) => {
                                            console.error('Failed to fetch age premiums:', err);
                                            if (data.zipCode) {
                                                setValidationError('Invalid zip code, Please try again');
                                                setInternalPlans([]);
                                                setInternalShowPlans(false);
                                            } else if (plansFromApi && plansFromApi.length > 0) {
                                                setInternalPlans(plansFromApi);
                                                setInternalShowPlans(true);
                                                setValidationError(null);
                                            }
                                            setInternalLoading(false);
                                            setInternalShowLoadingAnimation(false);
                                            fetchingAgePremiumsRef.current = '';
                                            activeFetchRef.current = '';
                                            if (animationTimeout) clearTimeout(animationTimeout);
                                        });
                                    return;
                                }
                            }

                            else if (Array.isArray(result)) {
                                const allPlans: any[] = result;
                                if (allPlans.length > 0 && allPlans[0] && ('premium_18' in allPlans[0] || 'premiumAge18' in allPlans[0])) {
                                    plans18 = allPlans.map((p: any) => ({ ...p, premium: p.premium_18 || p.premiumAge18 || p.premium }));
                                    plans65 = allPlans.map((p: any) => ({ ...p, premium: p.premium_65 || p.premiumAge65 || p.premium }));
                                } else {

                                    plans18 = allPlans;
                                    plans65 = allPlans;
                                }
                            }
                            else {
                                const allPlans: FullPlan[] = result.plans ?? result;
                                plans18 = allPlans;
                                plans65 = allPlans;
                            }

                            processAgePremiums(plans18, plans65);

                            function processAgePremiums(plans18: FullPlan[], plans65: FullPlan[]) {
                                if (isStale()) return;
                                if (data.zipCode && (!plansFromApi || plansFromApi.length === 0) && (!plans18 || plans18.length === 0) && (!plans65 || plans65.length === 0)) {
                                    setValidationError('Invalid zip code, Please try again');
                                    setInternalPlans([]);
                                    setInternalShowPlans(false);
                                    setInternalLoading(false);
                                    setInternalShowLoadingAnimation(false);
                                    fetchingAgePremiumsRef.current = '';
                                    activeFetchRef.current = '';
                                    if (animationTimeout) clearTimeout(animationTimeout);
                                    return;
                                }

                                const plans18Map = new Map(plans18.map((p) => [p.id, p]));
                                const plans65Map = new Map(plans65.map((p) => [p.id, p]));

                                const plansWithAgePremiums: PlanWithAgePremiums[] = plansFromApi.map((plan) => ({
                                    ...plan,
                                    premiumAge18: plans18Map.get(plan.id)?.premium,
                                    premiumAge65: plans65Map.get(plan.id)?.premium,
                                }));



                                setInternalPlans(plansWithAgePremiums);
                                setInternalShowPlans(true);
                                setInternalLoading(false);
                                setInternalShowLoadingAnimation(false);
                                setValidationError(null);
                                fetchingAgePremiumsRef.current = '';
                                activeFetchRef.current = '';
                                if (animationTimeout) clearTimeout(animationTimeout);
                            }
                        })
                        .catch((err) => {
                            if (isStale()) return;
                            console.error('Failed to fetch age premiums:', err);
                            if (data.zipCode) {
                                setValidationError('Invalid zip code, Please try again');
                                setInternalPlans([]);
                                setInternalShowPlans(false);
                            } else if (plansFromApi && plansFromApi.length > 0) {
                                setInternalPlans(plansFromApi);
                                setInternalShowPlans(true);
                                setValidationError(null);
                            }
                            setInternalLoading(false);
                            setInternalShowLoadingAnimation(false);
                            fetchingAgePremiumsRef.current = '';
                            activeFetchRef.current = '';
                            if (animationTimeout) clearTimeout(animationTimeout);
                        });
                } else {
                    if (isStale()) return;
                    if (data.zipCode && (!plansFromApi || plansFromApi.length === 0)) {
                        setValidationError('Invalid zip code, Please try again');
                        setInternalPlans([]);
                        setInternalShowPlans(false);
                    } else {
                        setInternalPlans(plansFromApi);
                        setInternalShowPlans(true);
                        setValidationError(null);
                    }
                    setInternalLoading(false);
                    setInternalShowLoadingAnimation(false);
                    activeFetchRef.current = '';
                    if (animationTimeout) clearTimeout(animationTimeout);
                }
            } catch (err) {
                if (isStale()) return;
                console.error(err);
                if (data.zipCode) {
                    setValidationError('Invalid zip code. Please try again.');
                    setInternalPlans([]);
                    setInternalShowPlans(false);
                } else {
                    setInternalError('Unable to load plans. Please try again.');
                }
                setInternalLoading(false);
                setInternalShowLoadingAnimation(false);
                activeFetchRef.current = '';
                if (animationTimeout) clearTimeout(animationTimeout);
            }
        },
        [buildNextSearchParamsForSearch, countyOptions, navigate]
    );

    useEffect(() => {
        if (isUpdatingUrlRef.current) {
            isUpdatingUrlRef.current = false;
            return;
        }

        if (onShopPlans) {
            return;
        }

        let isCancelled = false;

        const hydrateFromUrl = async () => {
            const pathParams = parsePathParams();
            const zipCode = pathParams.zipCode || searchParams.get('zipCode');
            const ages = searchParams.getAll('age');

            let state = pathParams.state || searchParams.get('state');
            if (state && state.includes('-')) {
                state = state.split('-')[0].toUpperCase();
            }

            const countyFromUrl = pathParams.county || searchParams.get('county');
            let county = countyFromUrl;

            if (state && countyFromUrl) {
                const countyCode = await getCountyCodeBySlug(state, countyFromUrl);
                if (countyCode) {
                    county = countyCode;
                    if (!isCancelled) {
                        setSelectedCounty(countyCode);
                    }
                }
            }

            const isStateFromPath =
                pathParams.hasStatePath && !pathParams.isStateRootPath && Boolean(pathParams.state);
            const shouldBlockAutoLoad = activeTab === 'state-county' && !isStateFromPath;

            if (shouldBlockAutoLoad) {
                return;
            }

            const hasZipAgeParams = Boolean(zipCode || ages.length > 0);
            const hasStateCountyParams = Boolean(state || county);

            if (hasZipAgeParams) {
                const searchKey = `${zipCode || ''}-${ages.join(',')}-${state || ''}-${county || ''}`;

                if (lastSearchKeyRef.current !== searchKey) {
                    lastSearchKeyRef.current = searchKey;

                    const searchData: ShopPlansData = {};
                    if (zipCode) searchData.zipCode = zipCode;
                    if (ages.length > 0) {
                        searchData.ages = ages;
                        searchData.age = ages[0];
                    }
                    if (state) searchData.state = state;
                    if (county) searchData.county = county;

                    handleShopPlansInternal(searchData, { updateUrl: false });
                }
            } else if (hasStateCountyParams && isStateFromPath) {
                const searchKey = `${state || ''}-${county || ''}`;

                if (lastSearchKeyRef.current !== searchKey) {
                    lastSearchKeyRef.current = searchKey;

                    const searchData: ShopPlansData = {};
                    if (state) searchData.state = state;
                    if (county) searchData.county = county;

                    handleShopPlansInternal(searchData, { updateUrl: false });
                }
            } else if (hasStateCountyParams && !isStateFromPath) {
                lastSearchKeyRef.current = '';
            } else {
                lastSearchKeyRef.current = '';
            }
        };

        hydrateFromUrl().catch((error) => {
            console.error('Failed to hydrate shop plans from URL:', error);
        });

        return () => {
            isCancelled = true;
        };
    }, [searchParamsKeyWithoutCompare, location.pathname, onShopPlans, handleShopPlansInternal, activeTab]);

    useEffect(() => {
        // After initial URL-driven hydration, allow URL updates from real user actions.
        isHydratingFromUrlRef.current = false;
    }, []);

    const handleShopPlans = () => {

        setFieldErrors({});

        let shopPlansData: ShopPlansData;

        if (activeTab === 'age-zip') {
            const hasZipCode = zipCode.trim() !== '';
            const validAges = ages.filter((value) => value.trim() !== '');
            const primaryAge = validAges.length > 0 ? validAges[0] : undefined;

            if (!hasZipCode) {
                setValidationError('Fill in the required fields to continue');
                setFieldErrors({
                    zipCode: true,
                    age: false,
                });
                return;
            }

            const familyMembersCount = validAges.length > 0 ? validAges.length : undefined;

            shopPlansData = {
                zipCode,
                age: primaryAge,
                ages: validAges.length > 0 ? validAges : undefined,
                familyMembersCount,
            };
        } else {
            if (!selectedState.trim()) {
                setValidationError('Fill in the required fields to continue');
                setFieldErrors({
                    state: true,
                });
                return;
            }

            shopPlansData = { state: selectedState, county: selectedCounty };
        }

        if (onShopPlans) {
            onShopPlans(shopPlansData);
        } else {
            handleShopPlansInternal(shopPlansData, { updateUrl: true });
        }
    };

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleShopPlans();
    };

    const tabs = [
        {
            value: 'age-zip',
            label: 'Search by Zip & Age',
        },
        {
            value: 'state-county',
            label: 'Browse by State & County',
        },
    ];

    return (
        <>
            <section
                ref={stickyBarBoundaryRef}
                className={`sticky bg-white shadow-sm ${hasStickyBarTakenOver ? 'top-0 z-60' : 'z-40'}`}
                style={hasStickyBarTakenOver ? undefined : { top: 'var(--site-header-height, 0px)' }}
            >
                <div
                    className="w-full bg-cover bg-center bg-no-repeat pt-1.5 mb-4"
                    style={{ backgroundImage: `url(${featuresBackground})` }}
                >
                    <div className="mx-auto px-5 md:px-10">
                        <div className="w-full min-w-0">
                            <div role="tablist" className="flex gap-2 overflow-x-auto">
                                {tabs.map((tab, idx) => (
                                    <button
                                        key={idx}
                                        role="tab"
                                        type="button"
                                        className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-t-lg transition-colors cursor-pointer whitespace-nowrap shrink-0 ${tab.value === activeTab
                                            ? 'bg-gray-50 text-brand-700'
                                            : 'bg-brand-100 text-brand-500'
                                            }`}
                                        onClick={() => {
                                            handleTabChange(tab.value);
                                        }}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="px-5 pt-0 pb-5 md:px-10">
                    <div className="flex w-full flex-col gap-4 md:flex-row md:items-end">
                        <div className="flex-1">
                            {activeTab === 'age-zip' ? (
                                <form onSubmit={handleFormSubmit} className="flex min-w-0 flex-col gap-2 sm:gap-4">
                                    <div className="flex min-w-0 flex-wrap gap-2 sm:gap-4 md:flex-wrap md:justify-start md:items-end max-md:flex-nowrap max-md:items-end">
                                        {/* Phone: inputs row should share space with buttons (no extra row) */}
                                        <div className="flex items-center gap-2 flex-nowrap overflow-x-auto w-full md:w-auto max-md:w-auto max-md:flex-1 max-md:min-w-0">
                                            <div className="w-[90px] shrink-0">
                                                <Input
                                                    id="zip-code"
                                                    label="Zip Code"
                                                    type="text"
                                                    placeholder="51234"
                                                    value={zipCode}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                                                        setZipCode(value);
                                                        if (fieldErrors.zipCode) {
                                                            setFieldErrors((prev) => ({ ...prev, zipCode: false }));
                                                            setValidationError(null);
                                                        }
                                                    }}
                                                    error={fieldErrors.zipCode ? '' : undefined}
                                                    inputWrapClassName={fieldErrors.zipCode ? '!border-red-500' : ''}
                                                />
                                            </div>
                                            <div className="flex items-end gap-1 flex-nowrap">
                                                {ages.map((age, index) => {
                                                    const isLast = index === ages.length - 1;
                                                    const isPrimaryAge = index === 0;

                                                    return (
                                                        <div key={index} className="flex shrink-0 items-end gap-2">
                                                            <div className="w-[50px] shrink-0">
                                                                <Input
                                                                    id={index === 0 ? 'age' : `age-${index + 1}`}
                                                                    label={isPrimaryAge ? "Age" : undefined}
                                                                    placeholder="27"
                                                                    value={age}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                                                                        const nextAges = [...ages];
                                                                        nextAges[index] = value;
                                                                        setAges(nextAges);
                                                                        if (isPrimaryAge && fieldErrors.age) {
                                                                            setFieldErrors((prev) => ({ ...prev, age: false }));
                                                                            setValidationError(null);
                                                                        }
                                                                    }}
                                                                    error={isPrimaryAge && fieldErrors.age ? '' : undefined}
                                                                    inputWrapClassName={
                                                                        isPrimaryAge && fieldErrors.age ? '!border-red-500' : ''
                                                                    }
                                                                />
                                                            </div>
                                                            {isLast && ages.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setAges((prev) => prev.slice(0, -1))}
                                                                    className="mb-[13px] hidden md:block cursor-pointer"
                                                                    aria-label="Remove family member"
                                                                >
                                                                    <svg
                                                                        width="12"
                                                                        height="12"
                                                                        viewBox="0 0 12 12"
                                                                        fill="none"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                    >
                                                                        <path
                                                                            d="M11.25 0.75L0.75 11.25"
                                                                            stroke="black"
                                                                            strokeWidth="1.5"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                        <path
                                                                            d="M11.25 11.25L0.75 0.75"
                                                                            stroke="black"
                                                                            strokeWidth="1.5"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {ages.length < 6 && (
                                                <div className="flex items-center justify-center mt-7">
                                                    <Elements.Button
                                                        variant="link"
                                                        type="button"
                                                        className="h-[27px] rounded-lg p-0 px-2 whitespace-nowrap no-underline"
                                                        onClick={() => setAges((prev) => [...prev, ''])}
                                                    >
                                                        <span className="text-base text-brand-700 sm:text-base">+ Add</span>
                                                    </Elements.Button>
                                                </div>
                                            )}
                                        </div>

                                        <div className="hidden md:flex items-end gap-2 w-full md:w-auto md:ml-[50px] shrink-0">
                                            <Elements.Button
                                                type="submit"
                                                variant="primary"
                                                size="sm"
                                                onClick={handleShopPlans}
                                                className="whitespace-nowrap max-w-[164px] w-full shrink-0"
                                            >
                                                Shop Plans
                                            </Elements.Button>
                                            <Elements.ShareDropdown
                                                url={shareUrl}
                                                title="Health Insurance Plans"
                                                buttonLabel="Share"
                                                size="sm"
                                                className="shrink-0 md:w-full max-w-[75px]"
                                            />
                                            {/*
                                            <Elements.Button
                                                type="button"
                                                variant="primary"
                                                outline={true}
                                                size="sm"
                                                onClick={() => window.print()}
                                                className="whitespace-nowrap shrink-0 md:w-full max-w-[75px]"
                                                aria-label="Print"
                                            >
                                                Print
                                            </Elements.Button>
                                            */}
                                            <Elements.Button
                                                type="button"
                                                variant="default"
                                                size="sm"
                                                onClick={handleStartOver}
                                                className="flex items-center gap-2 max-w-[130px] w-full bg-transparent border border-gray-300 text-[#8D939A] shrink-0 hover:bg-gray-50"
                                                aria-label="Start Over"
                                            >
                                                <Image src={StartOver} alt="Start Over" width={16} height={16} />
                                                <span className="text-gray-900">Start Over</span>
                                            </Elements.Button>  
                                        </div>
                                        {/* Mobile buttons */}
                                        <div className="flex items-end gap-2 md:hidden flex-none shrink-0 justify-end" style={{ marginTop: 0 }}>
                                            <Elements.Button
                                                type="submit"
                                                variant="primary"
                                                size="sm"
                                                onClick={handleShopPlans}
                                                className="whitespace-nowrap max-w-[70px] w-full shrink-0"
                                            >
                                                Shop
                                            </Elements.Button>
                                            <Elements.Button
                                                type="button"
                                                variant="primary"
                                                outline={true}
                                                size="sm"
                                                onClick={() => {
                                                    const event = new CustomEvent('openFiltersModal');
                                                    window.dispatchEvent(event);
                                                }}
                                                className="shrink-0 max-w-[44px] w-full"
                                                aria-label="Filter"
                                            >
                                                <Image src={FilterIcon} alt="Filter" width={16} height={16} />
                                            </Elements.Button>
                                        </div>
                                    </div>
                                    {validationError && <div className="-mt-2 text-sm font-medium text-red-600">{validationError}</div>}
                                </form>
                            ) : (
                                <form onSubmit={handleFormSubmit} className="flex min-w-0 flex-col gap-2 sm:gap-4" style={{ overflow: 'visible' }}>
                                    <div className="flex min-w-0 flex-wrap gap-2 sm:gap-4 md:flex-wrap md:justify-start md:items-end md:gap-2">
                                        <div className="relative flex-1 min-w-0 sm:flex-none sm:w-[230px] md:w-[180px]" style={{ overflow: 'visible' }}>
                                            <StateSelect
                                                id="state"
                                                label="State"
                                                value={selectedState}
                                                onChange={(value) => {
                                                    // Ensure we stay on state-county tab when selecting state
                                                    if (activeTab !== 'state-county') {
                                                        setActiveTab('state-county');
                                                    }
                                                    setSelectedState(value);
                                                    if (fieldErrors.state) {
                                                        setFieldErrors((prev) => ({ ...prev, state: false }));
                                                        setValidationError(null);
                                                    }
                                                }}
                                                placeholder="Search state..."
                                                error={fieldErrors.state ? '' : undefined}
                                                btnClassName={fieldErrors.state ? '!border-red-500' : ''}
                                                maxHeight="220px"
                                            />
                                        </div>
                                        <div className="relative flex-1 min-w-0 sm:flex-none sm:w-[280px] md:w-[180px] lg:w-[280px]" style={{ overflow: 'visible' }}>
                                            <Select
                                                id="county"
                                                label="County (Optional)"
                                                options={countyOptions}
                                                value={selectedCounty}
                                                onChange={(value) => setSelectedCounty(value as string)}
                                                placeholder={
                                                    isLoadingCounties ? 'Loading...' : selectedState ? 'Search county...' : 'Select state first'
                                                }
                                                disabled={isLoadingCounties}
                                                searchable={true}
                                                maxHeight="220px"
                                            />
                                        </div>
                                        {/* Desktop buttons */}
                                        <div className="hidden md:flex items-end gap-2 w-full md:w-auto md:ml-2 lg:ml-[50px] shrink-0">
                                            <Elements.Button
                                                type="submit"
                                                variant="primary"
                                                size="sm"
                                                onClick={handleShopPlans}
                                                className="whitespace-nowrap max-w-[164px] w-full shrink-0"
                                            >
                                                Shop Plans
                                            </Elements.Button>
                                            <Elements.ShareDropdown
                                                url={shareUrl}
                                                title="Health Insurance Plans"
                                                buttonLabel="Share"
                                                size="sm"
                                                className="shrink-0 max-w-[70px] w-full"
                                            />
                                            {/*
                                            <Elements.Button
                                                type="button"
                                                variant="primary"
                                                outline={true}
                                                size="sm"
                                                onClick={() => window.print()}
                                                className="whitespace-nowrap shrink-0 max-w-[70px] w-full"
                                                aria-label="Print"
                                            >
                                                Print
                                            </Elements.Button>
                                            */}
                                            <Elements.Button
                                                type="button"
                                                variant="default"
                                                size="sm"
                                                onClick={handleStartOver}
                                                className="flex items-center gap-2 bg-transparent border border-gray-300 text-[#8D939A] shrink-0 hover:bg-gray-50"
                                                aria-label="Start Over"
                                            >
                                                <Image src={StartOver} alt="Start Over" width={16} height={16} />
                                                <span className="text-gray-900">Start Over</span>
                                            </Elements.Button>
                                        </div>
                                    </div>
                                    {validationError && <div className="-mt-2 text-sm font-medium text-red-600">{validationError}</div>}
                                    {/* Mobile buttons */}
                                    <div className="flex items-end gap-2 md:hidden w-full" style={{ marginTop: 0 }}>
                                        <Elements.Button
                                            type="submit"
                                            variant="primary"
                                            size="sm"
                                            onClick={handleShopPlans}
                                            className="whitespace-nowrap flex-1 min-w-0"
                                        >
                                            Shop
                                        </Elements.Button>
                                        <Elements.Button
                                            type="button"
                                            variant="primary"
                                            outline={true}
                                            size="sm"
                                            onClick={() => {
                                                const event = new CustomEvent('openFiltersModal');
                                                window.dispatchEvent(event);
                                            }}
                                            className="shrink-0 w-[44px]"
                                            aria-label="Filter"
                                        >
                                            <Image src={FilterIcon} alt="Filter" width={16} height={16} />
                                        </Elements.Button>
                                    </div>
                                </form>
                            )}
                        </div>
                        <div className="hidden lg:flex items-end shrink-0 ml-8">
                            <Image src={LogoRightSide} alt="Predictable Benefits" width={193} height={50} />
                        </div>
                    </div>
                </div>
            </section>

            {!effectiveShowPlans && !effectiveLoading && (
                <section className="w-full flex justify-center py-10 md:py-20">
                    <div className="w-full max-w-[1140px] px-5 md:px-10">
                        {activeTab === 'state-county' && isDesktop && (
                            <div className="hidden lg:block">
                                <Elements.StateMap />
                            </div>
                        )}
                        {activeTab === 'age-zip' && (
                            <div className="w-full text-center py-10">
                                <Elements.Title
                                    title="Enter a Zip Code and an Age"
                                    type="h2"
                                    className="text-center mb-4"
                                />
                                <Elements.Description
                                    description="Click Shop Plans to see results"
                                    type="medium"
                                    className="text-center text-gray-600"
                                />
                            </div>
                        )}
                        {activeTab === 'state-county' && (
                            <div className="lg:hidden w-full text-center py-10">
                                <Elements.Title
                                    title="Select State and County"
                                    type="h2"
                                    className="text-center mb-4"
                                />
                                <Elements.Description
                                    description="Click Shop Plans to see results"
                                    type="medium"
                                    className="text-center text-gray-600"
                                />
                            </div>
                        )}
                    </div>
                </section>
            )}

            {(effectiveLoading || (error && effectiveShowPlans)) && (
                <div
                    className="transition-opacity duration-300 ease-in-out"
                    style={{ opacity: effectiveLoading || (error && effectiveShowPlans) ? 1 : 0 }}
                >
                    {effectiveShowLoadingAnimation || error ? (
                        <div id="plans-section">
                            <SkeletonPlansSection isStateCountrySearch={isStateCountrySearch} />
                        </div>
                    ) : (
                        <div id="plans-section">
                            <PlansSection
                                plans={[]}
                                isStateCountrySearch={isStateCountrySearch}
                                familyAges={familyAges}
                                loading={true}
                                activeTab={activeTab}
                            />
                        </div>
                    )}
                </div>
            )}

            {effectiveShowPlans && !effectiveLoading && (
                <div
                    id="plans-section"
                    className="transition-opacity duration-300 ease-in-out"
                    style={{ opacity: isContentVisible ? 1 : 0 }}
                >
                    <PlansSection
                        plans={plans}
                        isStateCountrySearch={isStateCountrySearch}
                        familyAges={familyAges}
                        loading={false}
                        activeTab={activeTab}
                    />
                </div>
            )}
        </>
    );
};

